import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const SliderWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Slide = styled.div`
  position: absolute;
  top: 10%;
  left: 50%;
  transform: ${({ offset }) =>
    `translateX(-50%) translateY(${offset * 40}px) scale(${
      offset === 0 ? 1 : 0.75
    })`};
  z-index: ${({ offset }) => (offset === 0 ? 2 : 1)};
  transition: all 0.6s ease;
  width: 100%;
  height: 180px;
  color: ${({ textColor = "white" }) => textColor};
  background: ${({ offset, generalBgColor, sideBgColor }) =>
    offset === 0 ? generalBgColor : sideBgColor};
  ${"" /* border-radius: 20px; */}
  border: 2px solid ${({ borderColor = "red" }) => borderColor};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 1;
  filter: ${({ offset }) => (offset === 0 ? "none" : "blur(2px)")};
`;

const MessageText = styled.div`
  font-size: ${({ fontSize }) => fontSize}rem;
  text-align: center;
  font-weight: bold;
  color: ${({ textColor = "white" }) => textColor};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  padding: 0 20px;
`;

const calculateDisplayTime = (msg) => {
  const text = msg.title ?? msg.messageDetails?.text ?? "";
  const wordCount = text.trim().split(/\s+/).length;

  if (wordCount <= 10) return 6000;
  if (wordCount <= 20) return 12000;

  const extraGroups = Math.ceil((wordCount - 20) / 10);
  return 12000 + extraGroups * 5000;
};

const calculateFontSize = (msg) => {
  const text = msg.title ?? msg.messageDetails?.text ?? "";
  const wordCount = text.trim().split(/\s+/).length;

  if (wordCount <= 10) return 1.8;
  if (wordCount <= 20) return 1.5;
  if (wordCount <= 30) return 1.3;
  if (wordCount <= 40) return 1.2;
  return 1.1;
};

const MessageSlider = ({
  initialMessages = [],
  background = "rgba(0, 0, 0, 0.5)",
  textColor = "white",
  titleBackground = "black",
  neonBackground = "#ffffff",
  neonText = "black",
  neonColor1 = "#ff007f",
  neonColor2 = "#00e0ff",
  birthdayColor1 = "#ff007f",
  birthdayColor2 = "#00e0ff",
  fontFamily = "assistant",
}) => {
  /* מאחד את שתי המערכים ושומר רק אובייקטים תקינים */
  const messages = useMemo(
    () => [...initialMessages].filter(Boolean),
    [initialMessages]
  );

  if (!messages.length) return null; // אין מה להציג

  const [index, setIndex] = useState(0);
  const getSlide = (offset) =>
    messages[(index + offset + messages.length) % messages.length];

  useEffect(() => {
    const currentMsg = messages[index];
    const delay = calculateDisplayTime(currentMsg);

    const id = setTimeout(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, delay);

    return () => clearTimeout(id);
  }, [index, messages]);

  return (
    <SliderWrapper>
      {[-1, 0, 1].map((offset) => {
        const msg = getSlide(offset);
        if (!msg) return null;

        // 1. חשיפת ה־_id או fallback
        const baseId = msg._id ?? msg.original?._id ?? "no-id";

        // 2. בניית key ייחודי עם offset
        const key = `${baseId}-${offset}`;

        const generalBgColor = neonBackground;
        const sideBgColor = background;
        const borderColor = offset === 0 ? neonColor1 : neonColor2;
        const slideTextColor = offset === 0 ? neonText : textColor;

        return (
          <Slide
            key={key}
            offset={offset}
            generalBgColor={generalBgColor}
            sideBgColor={sideBgColor}
            borderColor={borderColor}
            textColor={slideTextColor}
          >
            <MessageText
              textColor={slideTextColor}
              style={{ fontFamily }}
              fontSize={calculateFontSize(msg)}
            >
              {msg.title ?? msg.messageDetails?.text ?? ""}
            </MessageText>
          </Slide>
        );
      })}
    </SliderWrapper>
  );
};

MessageSlider.propTypes = {
  initialMessages: PropTypes.array,
  birthdayMessages: PropTypes.array,
  background: PropTypes.string,
  textColor: PropTypes.string,
  titleBackground: PropTypes.string,
  neonBackground: PropTypes.string,
  neonText: PropTypes.string,
  neonColor1: PropTypes.string,
  neonColor2: PropTypes.string,
  birthdayColor1: PropTypes.string,
  birthdayColor2: PropTypes.string,
  fontFamily: PropTypes.string,
};

export default MessageSlider;
